import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { TriangleAlert } from 'lucide-react';
import { eventBus } from '../events';
import { isLiveDemo, isScouting } from '../utils/capabilities';
/**
 * Non-collapsible warning pill pinned to the top-center of the map while viewing
 * an expired demo in low-resolution "scouting" mode. Reads the gate's own
 * verdict (accessLevel === 'scouting'); a live demo that ticked past expiry
 * since the last fetch is treated as scouting too. The CTA opens the unlock
 * flow (same event the control panel uses).
 */
export const LowResScoutingPill = ({ project }) => {
    const showPill = useMemo(() => {
        if (!project)
            return false;
        const expiredSinceFetch = isLiveDemo(project.accessLevel) && project.demoExpiresAt != null && Date.now() / 1000 >= project.demoExpiresAt;
        const isLowRes = isScouting(project.accessLevel) || expiredSinceFetch;
        // Only once the low-res set actually exists — during the conversion window
        // (scoutingGeneratedAt null) the control panel shows a "being converted"
        // spinner instead, so the pill claiming "low-res available" would contradict it.
        const scoutingReady = project.scoutingGeneratedAt != null;
        return isLowRes && scoutingReady;
    }, [project]);
    if (!showPill || !project)
        return null;
    const handleUnlock = () => {
        eventBus.emit('project:openUnlockModal', {
            projectId: project.id,
            projectName: project.name,
            demoExpiresAt: project.demoExpiresAt,
        });
    };
    return (_jsx("div", { className: "absolute left-1/2 top-3 -translate-x-1/2 z-20 pointer-events-auto", role: "status", children: _jsxs("div", { className: "flex items-center gap-3 rounded-full px-4 py-2 shadow-lg border", style: { backgroundColor: '#181818', borderColor: 'var(--pewter, #3a3a3a)' }, children: [_jsx(TriangleAlert, { size: 18, className: "shrink-0", style: { color: 'var(--sand, #e7c873)' }, "aria-hidden": true }), _jsxs("div", { className: "flex flex-col items-center text-center leading-tight", children: [_jsx("span", { className: "text-xs font-semibold", style: { color: 'var(--sand, #e7c873)' }, children: "Low-resolution demo \u2013 not suitable for design decisions" }), _jsx("span", { className: "text-[11px]", style: { color: 'var(--dew, #aab2ad)' }, children: "To restore full-resolution and exports, unlock this project." })] }), _jsx("button", { onClick: handleUnlock, className: "shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors", style: { backgroundColor: 'var(--sand, #e7c873)', color: '#181818' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--dew, #aab2ad)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'var(--sand, #e7c873)'; }, children: "Unlock" })] }) }));
};
